/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { IFieldDefinitionPanelCheckboxData, IFieldDefinitionPanelCheckboxState } from ".";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { BaseCommands, FormErrors, IFieldChangeData } from "../../State";
import { CmdBuilder } from "@nuance/hap-components";

const logger: ILogger = getLogger();

export class FieldDefinitionPanelCheckboxCommands extends BaseCommands<
    IFieldDefinitionPanelCheckboxData,
    IFieldDefinitionPanelCheckboxState
> {
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd<IFieldDefinitionPanelCheckboxState, string>(this.OnBlurHandler),
            onTextChanged: cb.bindCmd<IFieldDefinitionPanelCheckboxState, IFieldChangeData>(
                this.OnTextChangedHandler
            ),
            onDefaultValueChanged: cb.bindCmd(this.onDefaultValueChangedHandler),
            onAccessLevelChanged: cb.bindCmd(this.OnAccessLevelChangedHandler),
            onHelpTextChange: cb.bindCmd(this.OnHelpTextChangeHandler),
            onAuditChanged: cb.bindCmd(this.OnAuditChangedHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IFieldDefinitionPanelCheckboxState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    Save = (state: IFieldDefinitionPanelCheckboxState): IFieldDefinitionPanelCheckboxData => {
        logger.logInformation("Saving checkbox properties");

        // important to make a copy!
        return { ...state.data };
    };

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(
        state: IFieldDefinitionPanelCheckboxState
    ): FormErrors<IFieldDefinitionPanelCheckboxData> {
        const errors: FormErrors<IFieldDefinitionPanelCheckboxData> = {};

        // settingName validation
        if (state.data.settingName === "") {
            errors.settingName = "Setting name is required.";
        } else {
            if (state.common.validateSettingName === undefined) {
                logger.logError("Setting name validation function not hooked up");
                throw new Error("Unable to validate setting name");
            }

            errors.settingName = state.common.validateSettingName(
                state.data.settingName,
                state.common.originalSettingName
            );
        }

        if (state.data.title === "") {
            errors.title = "Label is required.";
        }

        if (!state.data.helpTextValid) {
            errors.helpTextValid = "Help text is not valid";
        }

        return errors;
    }

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    onDefaultValueChangedHandler = (
        state: IFieldDefinitionPanelCheckboxState,
        newValue: boolean
    ) => {
        state.isDirty = true;
        state.data.defaultValue = newValue;
        return state;
    };
}
